import React from "react";

import { Grid, Link as MuiLink, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";

import ShipsTogetherBadge from "./ShipsTogetherBadge";
import {
  OutboundStatus,
  OutboundStatusLabels,
} from "../../../utils/constants";
import { isLinked, shipWithSiblingIds } from "../../../utils/shipWith";
import useShipWithMembers from "../../../utils/useShipWithMembers";
import { deepRedirect } from "../../../utils/utils";

const useStyles = makeStyles((theme) => ({
  heading: {
    display: "flex",
    alignItems: "center",
  },
  badge: {
    marginLeft: theme.spacing(1),
  },
  sibling: {
    marginBottom: theme.spacing(0.5),
  },
  // MUI renders component="button" links centred; the aside is left-aligned.
  link: {
    textAlign: "left",
  },
}));

/** The same wording statusRenderer uses for the record's own status line. */
export const shipWithStatusLabel = (status) => {
  if (status === OutboundStatus.ADVICE_PENDING) return "Pending Review";
  if (status === OutboundStatus.ADVICE_REJECTED) return "Rejected";
  return OutboundStatusLabels[status] || status || "";
};

/** "Acme Lumber · Pending" — consignee first, since that is what tells the
 * loads on one truck apart. */
export const getSiblingCaption = (member) =>
  [member?.shipTo?.name, shipWithStatusLabel(member?.status)]
    .filter(Boolean)
    .join(" · ");

/**
 * The "Ships Together:" block of the outbound asides (AD-8): the badge next to
 * the overline and one line per sibling — never the record itself — whose PO
 * number opens the sibling's form through deepRedirect, so the breadcrumb
 * grows and its BackButton returns here. Nothing renders for a singleton.
 *
 * `editPath` is the form's base path ("/outboundShipments"), or a function of
 * the member for a portal whose list routes by status.
 */
export default function ShipsTogetherAside({
  record,
  history,
  backPath,
  editPath,
}) {
  const classes = useStyles();
  const { members, loading } = useShipWithMembers(record);

  if (!isLinked(record)) return null;

  const siblingIds = shipWithSiblingIds(record);
  const byId = new Map(members.map((member) => [member.id, member]));

  const resolvePath = (member) =>
    typeof editPath === "function"
      ? editPath(member)
      : `${editPath}/${member.id}`;

  const openSibling = (member) => {
    if (!history) return;
    deepRedirect(history, resolvePath(member), backPath);
  };

  return (
    <Grid item xs={12}>
      <div className={classes.heading}>
        <Typography variant="overline" color="textSecondary">
          Ships Together:
        </Typography>
        <ShipsTogetherBadge
          record={record}
          selfId={record.id}
          size="small"
          withTooltip={false}
          className={classes.badge}
        />
      </div>
      {siblingIds.map((id) => {
        if (loading) {
          return (
            <Skeleton
              key={id}
              variant="text"
              width="60%"
              data-testid="ships-together-skeleton"
            />
          );
        }
        const member = byId.get(id);
        if (!member || member.unavailable) {
          return (
            <Typography
              key={id}
              variant="body2"
              color="textSecondary"
              className={classes.sibling}
            >
              PO unavailable
            </Typography>
          );
        }
        return (
          <div key={id} className={classes.sibling}>
            <MuiLink
              component="button"
              variant="body2"
              color="primary"
              className={classes.link}
              onClick={() => openSibling(member)}
            >
              {member.poNumber || "No PO number"}
            </MuiLink>
            <Typography variant="caption" color="textSecondary" display="block">
              {getSiblingCaption(member)}
            </Typography>
          </div>
        );
      })}
    </Grid>
  );
}
